import { useSubscription } from '@/shared/hooks/useBilling';
import { useAccountState } from '@/shared/hooks/useAccountState';
import { PLAN_FEATURES, PLAN_LEVEL } from '@unipostudio/platform-constants';
import type { PlanId, SellablePlanId } from '@/shared/services/billing.service';

/**
 * A level a gate can ask for, mirroring the API's @RequiresPlan levels.
 * `classic` means "any paid plan": grandfathered Classic, Pro and Agency all
 * pass it, the free Starter plan does not (ADR-007).
 */
export type PlanTier = 'classic' | 'pro' | 'agency';

/** Ladder order, from the shared PLAN_LEVEL. Starter sits below every paid plan. */
export const PLAN_LEVELS: Readonly<Record<PlanId, number>> = {
  starter: PLAN_LEVEL.STARTER,
  classic: PLAN_LEVEL.CLASSIC,
  pro: PLAN_LEVEL.PRO,
  agency: PLAN_LEVEL.AGENCY,
};

/**
 * The plan to offer for a gate: the cheapest plan sold that passes it.
 * Classic is not sold, so a `classic` gate offers Pro.
 */
export function upgradePlanFor(tier: PlanTier): SellablePlanId {
  return tier === 'agency' ? 'agency' : 'pro';
}

export interface PlanGateResult {
  allowed: boolean;
  /** The plan in force: starter, classic, pro or agency. */
  currentPlan: PlanId;
  requiredPlan: PlanTier;
  /** The plan an upgrade prompt offers (never Classic). */
  upgradePlan: SellablePlanId;
  isLoading: boolean;
}

/**
 * Whether the current brand's ACCOUNT plan meets `requiredPlan`. The plan
 * belongs to the account (ADR-006), so every brand of an account answers the
 * same. Mirrors the API's @RequiresPlan levels.
 */
export function usePlanGate(requiredPlan: PlanTier): PlanGateResult {
  const { isLoading } = useSubscription();
  const account = useAccountState();
  const currentPlan = account.plan;
  return {
    allowed: PLAN_LEVELS[currentPlan] >= PLAN_LEVELS[requiredPlan],
    currentPlan,
    requiredPlan,
    upgradePlan: upgradePlanFor(requiredPlan),
    isLoading: isLoading && !account.known,
  };
}

/**
 * Plan limits for frontend enforcement — DERIVED from the shared PLAN_FEATURES
 * matrix (@unipostudio/platform-constants), the same source the backend uses,
 * so there is one canonical definition instead of a drifting hardcoded copy.
 * Read synchronously by PlatformSelector, hence a plain module constant.
 */
export interface PlanLimits {
  platforms: number;
  postsPerMonth: number;
  teamSeats: number;
  aiGenerations: number;
  analyticsRetentionDays: number;
  storageMb: number;
}

function toLimits(feat: (typeof PLAN_FEATURES)[keyof typeof PLAN_FEATURES]): PlanLimits {
  return {
    platforms: feat.maxPlatforms,
    postsPerMonth: feat.postsPerMonth,
    teamSeats: feat.teamSeats,
    aiGenerations: feat.aiGenerations,
    analyticsRetentionDays: feat.analyticsRetentionDays,
    storageMb: Math.round(feat.storageBytes / (1024 * 1024)),
  };
}

export const PLAN_LIMITS: Readonly<Record<PlanId, PlanLimits>> = {
  starter: toLimits(PLAN_FEATURES.STARTER),
  classic: toLimits(PLAN_FEATURES.CLASSIC),
  pro: toLimits(PLAN_FEATURES.PRO),
  agency: toLimits(PLAN_FEATURES.AGENCY),
};
